import { jsPDF } from "jspdf";
import { getCellMongoId } from "@/lib/cellIds";
import { saveBlobAsFile } from "@/lib/dataIo";
import { memberSexLabel } from "@/lib/memberSex";
import {
  youthCampAttendedExportLabel,
  youthCampGroupLabel,
  MEMBER_YOUTH_CAMP_FILTER_ALL_GROUPS,
  spiritualStatusFilterLabel,
} from "@/lib/memberFormLabels";

export const MEMBER_EXPORT_COLUMNS = [
  { key: "name", header: "Nombre" },
  { key: "phone", header: "Teléfono" },
  { key: "sex", header: "Sexo" },
  { key: "cell_name", header: "Célula" },
  { key: "age", header: "Edad" },
  { key: "mentor", header: "Mentor" },
  { key: "member_type", header: "Tipo de miembro" },
  { key: "child_role", header: "Rol en enlace" },
  { key: "youth_camp_attended", header: "Campamento de Jóvenes" },
  { key: "youth_camp_group", header: "Grupo" },
];

const MEMBER_TYPE_LABELS = {
  invitado_potencial: "Invitado Potencial",
  invitado_primer_mes: "Invitado 1er Mes",
  visita_regular: "Visita Regular",
  visita_esporadica: "Visita Esporádica",
  visita: "Visita",
  miembro: "Miembro",
  invitado: "Invitado",
};

export function memberTypeLabelForExport(type) {
  return MEMBER_TYPE_LABELS[type] || type || "";
}

/** Título del reporte según filtro actual en Miembros. */
export function buildMembersFilterReportTitle({
  filterCell,
  filterMode,
  cells,
  filterYouthCamp,
  filterSpiritualStatus,
}) {
  let base;
  if (!filterCell) base = "Reporte de Miembros – Todas las células";
  else {
    const cell = (cells || []).find((c) => getCellMongoId(c) === filterCell);
    const name = cell?.name || filterCell;
    if (filterMode === "parent_enlace") {
      base = `Reporte de Miembros – ${name} (Célula madre / Enlace)`;
    } else {
      base = `Reporte de Miembros – ${name}`;
    }
  }
  const yf = String(filterYouthCamp || "").trim();
  if (yf) {
    if (yf === MEMBER_YOUTH_CAMP_FILTER_ALL_GROUPS) {
      base = `${base} – Campamento de Jóvenes (todos los grupos)`;
    } else {
      const groupLabel = youthCampGroupLabel(yf);
      if (groupLabel) base = `${base} – Campamento de Jóvenes (${groupLabel})`;
    }
  }
  const sf = String(filterSpiritualStatus || "").trim();
  if (sf) {
    const statusLabel = spiritualStatusFilterLabel(sf);
    if (statusLabel) base = `${base} – Estado espiritual (${statusLabel})`;
  }
  return base;
}

/**
 * @param {Array<Record<string, unknown>>} filteredMembers misma lista que en pantalla
 * @param {(cellId: string) => string} getCellName
 * @param {(member: object) => string} formatMentorFn
 */
export function buildMembersExportRows(filteredMembers, getCellName, formatMentorFn) {
  return (filteredMembers || []).map((m) => ({
    name: String(m.name || ""),
    phone: String(m.phone || ""),
    sex: memberSexLabel(m.sex),
    cell_name: getCellName(m.cell_id) || "Sin célula",
    age: m.age != null && m.age !== "" ? String(m.age) : "",
    mentor: formatMentorFn(m) || "",
    member_type: memberTypeLabelForExport(m.member_type),
    child_role: String(m.childLeaderRoleLabel || ""),
    youth_camp_attended: youthCampAttendedExportLabel(m.youth_camp_attended),
    youth_camp_group: m.youth_camp_attended ? youthCampGroupLabel(m.youth_camp_group) : "",
  }));
}

function stampFilename(base) {
  const d = new Date().toISOString().slice(0, 10);
  return `${base}_${d}`;
}

function escapeCsvCell(value) {
  const s = String(value ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function exportMembersFilteredCsv(rows, reportTitle) {
  const headerLine = MEMBER_EXPORT_COLUMNS.map((c) => escapeCsvCell(c.header)).join(",");
  const dataLines = rows.map((row) =>
    MEMBER_EXPORT_COLUMNS.map((c) => escapeCsvCell(row[c.key])).join(","),
  );
  const meta = `# ${reportTitle}`;
  const content = `\uFEFF${meta}\n${headerLine}\n${dataLines.join("\n")}\n`;
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  saveBlobAsFile(blob, `${stampFilename("miembros_filtrados")}.csv`);
}

export function exportMembersFilteredExcel(rows, reportTitle) {
  const th = MEMBER_EXPORT_COLUMNS.map((c) => `<th>${escapeHtml(c.header)}</th>`).join("");
  const trs = rows
    .map(
      (row) =>
        `<tr>${MEMBER_EXPORT_COLUMNS.map((c) => `<td>${escapeHtml(row[c.key])}</td>`).join("")}</tr>`,
    )
    .join("");
  const html = `\uFEFF<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
<head><meta charset="UTF-8"><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
<x:Name>Miembros</x:Name></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head>
<body><h3>${escapeHtml(reportTitle)}</h3><table border="1"><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table></body></html>`;
  const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" });
  saveBlobAsFile(blob, `${stampFilename("miembros_filtrados")}.xls`);
}

export function exportMembersFilteredPdf(rows, reportTitle) {
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const margin = 10;
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const usableW = pageW - margin * 2;

  const colWeights = [22, 18, 10, 22, 8, 20, 18, 16, 14, 12];
  const weightSum = colWeights.reduce((a, b) => a + b, 0);
  const colWidths = colWeights.map((w) => (usableW * w) / weightSum);
  const colX = colWidths.reduce((acc, w, i) => {
    if (i === 0) return [margin];
    acc.push(acc[i - 1] + colWidths[i - 1]);
    return acc;
  }, []);

  const lineH = 3.6;
  const headerH = 5;

  const drawTitle = (y) => {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.text(reportTitle, margin, y);
    return y + 7;
  };

  const drawTableHeader = (y) => {
    pdf.setFillColor(30, 64, 175);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    pdf.rect(margin, y - headerH + 1.5, usableW, headerH, "F");
    MEMBER_EXPORT_COLUMNS.forEach((col, i) => {
      const lines = pdf.splitTextToSize(col.header, colWidths[i] - 1);
      pdf.text(lines, colX[i] + 0.5, y);
    });
    pdf.setTextColor(0, 0, 0);
    pdf.setFont("helvetica", "normal");
    return y + 2;
  };

  let y = drawTitle(margin + 4);
  y = drawTableHeader(y + headerH);

  if (!rows.length) {
    pdf.setFontSize(9);
    pdf.text("No hay miembros para exportar con el filtro actual.", margin, y + 4);
    pdf.save(`${stampFilename("miembros_filtrados")}.pdf`);
    return;
  }

  rows.forEach((row, rowIndex) => {
    const wrapped = MEMBER_EXPORT_COLUMNS.map((col, i) =>
      pdf.splitTextToSize(String(row[col.key] ?? ""), colWidths[i] - 1),
    );
    const rowLines = Math.max(1, ...wrapped.map((lines) => lines.length));
    const rowH = rowLines * lineH + 1.5;

    if (y + rowH > pageH - margin) {
      pdf.addPage();
      y = margin;
      y = drawTableHeader(y + headerH);
    }

    if (rowIndex % 2 === 1) {
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin, y - 1, usableW, rowH, "F");
    }

    pdf.setFontSize(7);
    wrapped.forEach((lines, i) => {
      pdf.text(lines, colX[i] + 0.5, y + lineH);
    });
    y += rowH;
  });

  pdf.save(`${stampFilename("miembros_filtrados")}.pdf`);
}
